import { useEffect } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { useClientEnvironment } from '@/src/features/config/hooks';
import { useIsIdle } from '@/src/hooks/use-is-idle';
import { logger } from '@/src/internal/logger';

/**
 * Initializes the Datadog browser SDKs (RUM and Logs) and starts Session Replay recording.
 *
 * The configuration is read through {@link useClientEnvironment}, whose value is provided by the
 * {@link EnvironmentProvider}: on pages wrapped in a page-context wrapper the environment arrives
 * server-injected and is ready on the first effect cycle, so initialization is eager and error
 * forwarding, user-action tracking (including INP), and view activity tracking observe the page
 * from as early in its lifecycle as possible. On pages without an injected environment (the
 * NextJS error pages), the environment resolves through the fallback request and initialization
 * follows as soon as it is ready.
 *
 * Session Replay recording is the one deliberately deferred piece: its DOM serialization and
 * mutation instrumentation dominate the SDK's startup cost, so recording starts only once the
 * browser reports itself idle via {@link useIsIdle}. The first moments of a session are therefore
 * absent from replays as an accepted trade for keeping the main thread clear during hydration.
 */
export const useInitializeDatadog = () => {
  const { env, isReady } = useClientEnvironment();
  const isIdle = useIsIdle();

  useEffect(() => {
    const logUsageOfHookInTestEnv = () => {
      logger.error(
        'The useInitializeDatadog hook should not be used in a test environment, as it ' +
          'may cause unnecessary initialization of internal state and refs. Are you using ' +
          'the "MockApplicationConfig" component in your tests?',
        { env: process.env.NODE_ENV },
      );
    };

    if (process.env.NODE_ENV === 'test') {
      return logUsageOfHookInTestEnv();
    }
    if (process.env.NODE_ENV === 'development' || !isReady) {
      return;
    }
    if (env.datadog.appId && env.datadog.clientToken) {
      if (!datadogRum.getInitConfiguration()) {
        datadogRum.init({
          allowedTracingUrls: [
            url => url.startsWith('https://app.craftedu.com/api'),
            url => url.startsWith('https://ojl-tracker.dev.craftedu.com/api'),
            url => url.startsWith('https://ojl-tracker.test.craftedu.com/api'),
            url => url.startsWith('https://ojl-tracker.prod.craftedu.com/api'),
          ],
          applicationId: env.datadog.appId,
          clientToken: env.datadog.clientToken,
          defaultPrivacyLevel: 'allow',
          enableExperimentalFeatures: ['clickmap'],
          env: env.deploymentEnv,
          service: 'ojl-tracker',
          sessionReplaySampleRate: 100,
          sessionSampleRate: 100,
          site: 'datadoghq.com',
          trackResources: true,
          trackUserInteractions: true,
          version: env.appVersion ?? undefined,
        });

        datadogLogs.init({
          clientToken: env.datadog.clientToken,
          env: env.deploymentEnv,
          forwardConsoleLogs: 'all',
          forwardErrorsToLogs: true,
          service: 'ojl-tracker',
          sessionSampleRate: 100,
          site: 'datadoghq.com',
        });
      }

      if (isIdle) {
        datadogRum.startSessionReplayRecording();
      }
    } else {
      logger.error(
        'Datadog initialization skipped: missing appId or clientToken in the environment.',
        {
          appId: env.datadog.appId ?? 'missing',
          clientToken: env.datadog.clientToken ?? 'missing',
        },
      );
    }
  }, [env, isReady, isIdle]);
};
